import React, { useState, useEffect } from 'react';
import { Contrast, ZoomIn, ZoomOut, Volume2, VolumeX, Eye, EyeOff } from 'lucide-react';

interface AccessibilityToolbarProps {
  className?: string;
}

export const AccessibilityToolbar: React.FC<AccessibilityToolbarProps> = ({
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [settings, setSettings] = useState({
    highContrast: false,
    fontSize: 'normal', // 'small', 'normal', 'large', 'extra-large'
    soundEnabled: true,
    animationsEnabled: true
  });

  // Carregar configurações do localStorage
  useEffect(() => {
    const saved = localStorage.getItem('accessibility-settings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings(parsed);
        applySettings(parsed);
      } catch (error) {
        console.error('Erro ao carregar configurações de acessibilidade:', error);
      }
    }
  }, []);

  // Salvar configurações
  const saveSettings = (newSettings: typeof settings) => {
    localStorage.setItem('accessibility-settings', JSON.stringify(newSettings));
    applySettings(newSettings);
  };

  // Aplicar configurações visuais
  const applySettings = (settings: any) => {
    const root = document.documentElement;
    
    // Alto contraste
    if (settings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Tamanho da fonte
    root.classList.remove('font-small', 'font-normal', 'font-large', 'font-extra-large');
    root.classList.add(`font-${settings.fontSize}`);
    
    // Animações
    if (!settings.animationsEnabled) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }
  };

  const toggleHighContrast = () => {
    const newSettings = { ...settings, highContrast: !settings.highContrast };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  const changeFontSize = (direction: 'increase' | 'decrease') => {
    const sizes = ['small', 'normal', 'large', 'extra-large'];
    const currentIndex = sizes.indexOf(settings.fontSize);
    
    let newIndex;
    if (direction === 'increase' && currentIndex < sizes.length - 1) {
      newIndex = currentIndex + 1;
    } else if (direction === 'decrease' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else {
      return; // Já está no limite
    }
    
    const newSettings = { ...settings, fontSize: sizes[newIndex] };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  const toggleSound = () => {
    const newSettings = { ...settings, soundEnabled: !settings.soundEnabled };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  const toggleAnimations = () => {
    const newSettings = { ...settings, animationsEnabled: !settings.animationsEnabled };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  return (
    <div className={`fixed top-4 right-4 z-50 ${className}`}>
      {/* Botão de expansão */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        aria-label={isExpanded ? 'Fechar ferramentas de acessibilidade' : 'Abrir ferramentas de acessibilidade'}
        aria-expanded={isExpanded}
      >
        <Eye className="w-5 h-5" />
      </button>

      {/* Toolbar expandida */}
      {isExpanded && (
        <div 
          className="mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-60"
          role="toolbar"
          aria-label="Ferramentas de acessibilidade"
        >
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Acessibilidade
          </h3>
          
          <div className="space-y-3">
            {/* Alto contraste */}
            <button
              onClick={toggleHighContrast}
              className={`w-full flex items-center justify-between p-2 rounded-md text-sm transition-colors ${
                settings.highContrast 
                  ? 'bg-blue-100 text-blue-900' 
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
              aria-pressed={settings.highContrast}
              aria-label="Alternar modo de alto contraste"
            >
              <div className="flex items-center space-x-2">
                <Contrast className="w-4 h-4" />
                <span>Alto Contraste</span>
              </div>
              <div className={`w-4 h-4 rounded ${settings.highContrast ? 'bg-blue-600' : 'bg-gray-300'}`} />
            </button>

            {/* Tamanho da fonte */}
            <div className="space-y-2">
              <span className="text-sm font-medium text-gray-700">Tamanho do Texto</span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => changeFontSize('decrease')}
                  disabled={settings.fontSize === 'small'}
                  className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Diminuir tamanho do texto"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-600 min-w-16 text-center capitalize">
                  {settings.fontSize === 'extra-large' ? 'Extra Grande' : 
                   settings.fontSize === 'large' ? 'Grande' :
                   settings.fontSize === 'normal' ? 'Normal' : 'Pequeno'}
                </span>
                <button
                  onClick={() => changeFontSize('increase')}
                  disabled={settings.fontSize === 'extra-large'}
                  className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Aumentar tamanho do texto"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Som */}
            <button
              onClick={toggleSound}
              className={`w-full flex items-center justify-between p-2 rounded-md text-sm transition-colors ${
                settings.soundEnabled 
                  ? 'bg-blue-100 text-blue-900' 
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
              aria-pressed={settings.soundEnabled}
              aria-label="Alternar sons do sistema"
            >
              <div className="flex items-center space-x-2">
                {settings.soundEnabled ? (
                  <Volume2 className="w-4 h-4" />
                ) : (
                  <VolumeX className="w-4 h-4" />
                )}
                <span>Sons</span>
              </div>
              <div className={`w-4 h-4 rounded ${settings.soundEnabled ? 'bg-blue-600' : 'bg-gray-300'}`} />
            </button>

            {/* Animações */}
            <button
              onClick={toggleAnimations}
              className={`w-full flex items-center justify-between p-2 rounded-md text-sm transition-colors ${
                settings.animationsEnabled 
                  ? 'bg-blue-100 text-blue-900' 
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
              aria-pressed={settings.animationsEnabled}
              aria-label="Alternar animações"
            >
              <div className="flex items-center space-x-2">
                {settings.animationsEnabled ? (
                  <Eye className="w-4 h-4" />
                ) : (
                  <EyeOff className="w-4 h-4" />
                )}
                <span>Animações</span>
              </div>
              <div className={`w-4 h-4 rounded ${settings.animationsEnabled ? 'bg-blue-600' : 'bg-gray-300'}`} />
            </button>
          </div>

          {/* Fechar */}
          <button
            onClick={() => setIsExpanded(false)}
            className="w-full mt-3 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md"
          >
            Fechar
          </button>
        </div>
      )}
    </div>
  );
};

export default AccessibilityToolbar;
